import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'

// CRM «Жаңа оқушы жолы». Поля - как в crm.proto (camelCase), bff дописывает *Name и отдаёт null вместо 0/"".

export const CRM_POLL = 30000

export interface CrmSubjectRef {
    id: number
    titleKz: string
    titleRu: string
    kind: 'MAIN' | 'PROFILE' | null
    videoOnly: boolean
}

export interface CrmIntakeData {
    studentName: string
    studentPhone: string
    iin: string
    grade: string
    language: string
    parentName: string
    parentPhone: string
    packageCode: string
    subjectIds: number[]
    payments: string[]
    paidSum: number
    discountSum: number | null
    contractUrl: string | null
    quota: string | null
    sources: string[]
    amoUrl: string
    bookOrderNo: string | null
    bookSum: number | null
    note: string | null
    // v2: тип оплаты (PAY_TYPE), payments - способы (PAY_METHOD)
    payType: string | null
    nextPaySum: number | null
    // 0 = текущий пользователь (РОП сам)
    sellerId?: number | null
}

export interface CrmPhoto {
    id: number
    url: string
}

export interface CrmStep {
    id: number
    stepNo: number
    doneAt: string | null
    verifiedAt: string | null
    verifiedBy: number | null
    verifiedByName?: string | null
    photos: CrmPhoto[]
}

export type CrmFeedback = 'OK' | 'QUESTION' | 'PROBLEM' | 'NO_REACH'
export type CrmTrackStage = 'NEW' | 'TODO' | 'DOING' | 'DONE'

export interface CrmTrackView {
    id: number
    intakeId: number
    subject: CrmSubjectRef
    courseSubjectId: number
    headAt: string | null
    headDeadlineAt: string | null
    groupId: number | null
    groupTitle: string | null
    mentorId: number | null
    mentorName?: string | null
    assignedAt: string | null
    assignedBy: number | null
    assignedByName?: string | null
    mentorDeadlineAt: string | null
    finishedAt: string | null
    feedback: CrmFeedback | null
    feedbackNote: string | null
    steps: CrmStep[]
    doneCount: number
    verifiedCount: number
    stage: CrmTrackStage
    studentName: string
    studentPhone: string
    iin: string | null
    grade: string
    language: string
    parentName: string
    parentPhone: string
    courseId: number
    courseTitleKz: string
    courseTitleRu: string
    endDate: string | null
    login: string | null
    allSubjects: CrmSubjectRef[]
    note: string | null
    sellerId: number
    sellerName?: string | null
    createdAt: string
    approvedAt: string | null
}

export type CrmEventType =
    | 'CREATED'
    | 'EDITED'
    | 'APPROVED'
    | 'ASSIGNED'
    | 'PHOTO'
    | 'VERIFIED'
    | 'FINISHED'
    | 'NOTIFIED'
    | 'RETURNED'
    | 'OVERDUE'
    | 'REASSIGNED'
    | 'DELETED'
    | 'PAY_REPORTED'
    | 'PAY_CONFIRMED'
    | 'PAY_REJECTED'
    | 'ACCESS_SET'

export interface CrmEvent {
    at: string
    type: CrmEventType
    actorId: number | null
    actorName?: string | null
    subjectId: number | null
    stepNo: number | null
    targetUserId: number | null
    targetUserName?: string | null
    // REASSIGNED: старый и новый менеджер (bff)
    fromUserName?: string | null
    toUserName?: string | null
    extra: string | null
}

export type CrmIntakeStatus = 'MODERATION' | 'APPROVED' | 'RETURNED' | 'DELETED'

export type CrmPayState = 'NONE' | 'NO_DATE' | 'OK' | 'SOON' | 'LATE' | 'PENDING' | 'DONE'

export interface CrmPaymentRow {
    id: number
    monthNo: number
    amount: number
    sellerId: number | null
    createdAt: string
    status: 'PENDING' | 'CONFIRMED'
    confirmedAt: string | null
}

export interface CrmIntakeView {
    id: number
    data: CrmIntakeData
    subjects: CrmSubjectRef[]
    sellerId: number
    sellerName?: string | null
    status: CrmIntakeStatus
    createdAt: string
    editedAt: string | null
    moderatorDeadlineAt: string | null
    approvedAt: string | null
    approvedBy: number | null
    approvedByName?: string | null
    courseId: number | null
    courseTitleKz: string | null
    courseTitleRu: string | null
    endDate: string | null
    studentUserId: number | null
    login: string | null
    tempPassword: string | null
    confirmedSubjectIds: number[]
    tracks: CrmTrackView[]
    returnedAt: string | null
    returnedBy: number | null
    returnedByName?: string | null
    returnReason: string | null
    returnText: string | null
    returnFileUrl: string | null
    returnFileName: string | null
    timeline: CrmEvent[]
    editedAfterApprove: boolean
    installment: boolean
    accessUntil: string | null
    nextPaySum: number | null
    paidMonths: number | null
    paidTotal: number | null
    payState: CrmPayState | null
    pendingPaymentId: number | null
    pendingPaymentSum: number | null
    pendingPaymentAt: string | null
    deletedAt: string | null
    deletedBy: number | null
    deletedByName?: string | null
    deleteReason: string | null
    payments: CrmPaymentRow[] | null
}

export interface CrmDuplicate {
    intakeId: number
    studentName: string
    matchedBy: 'PHONE' | 'IIN' | null
}

export interface CrmAssignGroup {
    groupId: number
    title: string
    language: string | null
    mentors: { mentorId: number; mentorName: string | null; studentCount: number }[]
}

export interface CrmCountSum {
    key: string
    count: number
    sum: number
}

export interface CrmManagerStat {
    sellerId: number
    sellerName?: string | null
    count: number
    turnover: number
    avgCheck: number
    discount: number
    returns: number
    books: number
    todayCount: number
    todayTurnover: number
    lastSaleAt: string | null
}

export interface CrmSalesReport {
    count: number
    turnover: number
    avgCheck: number
    discount: number
    books: number
    returns: number
    returnsSum: number
    byDay: CrmCountSum[]
    byManager: CrmManagerStat[]
    byPackage: CrmCountSum[]
    bySource: CrmCountSum[]
    byPayment: CrmCountSum[]
    byPayType: CrmCountSum[]
    installmentTurnover: number
}

export interface CrmManager {
    userId: number
    name: string
    phone: string | null
    salesGroup: 'OP_KZ' | 'OP_RU' | null
    hiredAt: string | null
    firedAt: string | null
    fireReason: string | null
    intakesTotal: number | null
    stat: CrmManagerStat | null
}

export interface CrmCredentials {
    login: string | null
    tempPassword: string | null
}

export type CrmDictType = 'PACKAGE' | 'PAY_TYPE' | 'PAY_METHOD' | 'SOURCE'

export interface CrmDictionaryItem {
    type: CrmDictType
    code: string | null
    titleKz: string
    titleRu: string | null
    price: number | null
    active: boolean
    installment: boolean
    used: number | null
    sortOrder: number | null
}

export interface CrmBadges {
    moderatorNew: number
    moderatorOverdue: number
    headNew: number
    headOverdue: number
    headToVerify: number
    mentorOpen: number
    mentorOverdue: number
    returns: number
    sellerToday: number
    installmentsAlert: number
}

export interface CrmTelegram {
    enabled: boolean
    linked: boolean
    deepLink: string | null
}

export interface CrmPeriod {
    from?: string
    to?: string
}

// bff может отдать массив как есть или обёрнутым в объект proto-ответа ({ intakes: [...] })
const listOf = (key: string) => (response: any) => {
    const d = response?.data
    return Array.isArray(d) ? d : d?.[key] ?? []
}
const dataOf = (response: any) => response?.data

const clean = (params: Record<string, any>) =>
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '' && v !== 0))

export const crmApi = createApi({
    reducerPath: 'crmApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['Intake', 'Track', 'Badges', 'Report', 'Managers', 'Telegram', 'Dictionary', 'Installments'],
    endpoints: (build) => ({
        getCrmSubjects: build.query<CrmSubjectRef[], void>({
            query: () => ({ url: 'crm/subjects' }),
            transformResponse: listOf('subjects'),
        }),

        createIntake: build.mutation<CrmIntakeView, CrmIntakeData>({
            query: (body) => ({ url: 'crm/intakes', method: 'POST', body }),
            invalidatesTags: ['Intake', 'Badges', 'Report', 'Managers', 'Installments', 'Dictionary'],
            transformResponse: dataOf,
        }),

        updateIntake: build.mutation<CrmIntakeView, { id: number; data: CrmIntakeData }>({
            query: ({ id, data }) => ({ url: `crm/intakes/${id}`, method: 'PUT', body: data }),
            invalidatesTags: ['Intake', 'Track', 'Badges', 'Report', 'Managers', 'Installments', 'Dictionary'],
            transformResponse: dataOf,
        }),

        checkDuplicate: build.query<CrmDuplicate | null, { phone?: string; iin?: string; excludeId?: number }>({
            query: (params) => ({ url: 'crm/intakes/duplicate', params: clean(params) }),
            transformResponse: (r: any) => (r?.data && r.data.intakeId ? r.data : null),
        }),

        listIntakes: build.query<CrmIntakeView[], { tab?: string; from?: string; to?: string; sellerId?: number }>({
            query: (params) => ({ url: 'crm/intakes', params: clean(params) }),
            providesTags: ['Intake'],
            transformResponse: listOf('intakes'),
        }),

        getIntake: build.query<CrmIntakeView, number>({
            query: (id) => ({ url: `crm/intakes/${id}` }),
            providesTags: ['Intake'],
            transformResponse: dataOf,
        }),

        approveIntake: build.mutation<
            CrmIntakeView,
            { id: number; courseId: number; endDate: string; subjectIds: number[]; tempPassword: string; accessUntil?: string }
        >({
            query: ({ id, ...body }) => ({ url: `crm/intakes/${id}/approve`, method: 'POST', body }),
            invalidatesTags: ['Intake', 'Track', 'Badges', 'Installments'],
            transformResponse: dataOf,
        }),

        returnIntake: build.mutation<CrmIntakeView, { id: number; body: FormData }>({
            query: ({ id, body }) => ({ url: `crm/intakes/${id}/return`, method: 'POST', body }),
            invalidatesTags: ['Intake', 'Track', 'Badges', 'Report', 'Managers', 'Installments'],
            transformResponse: dataOf,
        }),

        deleteIntake: build.mutation<CrmIntakeView, { id: number; reason: string }>({
            query: ({ id, reason }) => ({ url: `crm/intakes/${id}/delete`, method: 'POST', body: { reason } }),
            invalidatesTags: ['Intake', 'Track', 'Badges', 'Report', 'Managers', 'Installments', 'Dictionary'],
            transformResponse: dataOf,
        }),

        listInstallments: build.query<CrmIntakeView[], 'SELLER' | 'MOD' | 'ROP'>({
            query: (scope) => ({ url: 'crm/installments', params: { scope } }),
            providesTags: ['Installments'],
            transformResponse: listOf('intakes'),
        }),

        reportPayment: build.mutation<CrmIntakeView, { intakeId: number; amount: number }>({
            query: ({ intakeId, amount }) => ({ url: `crm/intakes/${intakeId}/payments`, method: 'POST', body: { amount } }),
            invalidatesTags: ['Installments', 'Intake', 'Badges', 'Report', 'Managers'],
            transformResponse: dataOf,
        }),

        confirmPayment: build.mutation<CrmIntakeView, { paymentId: number; amount: number; nextDate: string; nextSum: number; last: boolean }>({
            query: ({ paymentId, ...body }) => ({ url: `crm/payments/${paymentId}/confirm`, method: 'POST', body }),
            invalidatesTags: ['Installments', 'Intake', 'Badges', 'Report', 'Managers'],
            transformResponse: dataOf,
        }),

        rejectPayment: build.mutation<CrmIntakeView, number>({
            query: (paymentId) => ({ url: `crm/payments/${paymentId}/reject`, method: 'POST' }),
            invalidatesTags: ['Installments', 'Intake', 'Badges', 'Report', 'Managers'],
            transformResponse: dataOf,
        }),

        setAccessUntil: build.mutation<CrmIntakeView, { intakeId: number; accessUntil: string; nextSum: number }>({
            query: ({ intakeId, ...body }) => ({ url: `crm/intakes/${intakeId}/access`, method: 'POST', body }),
            invalidatesTags: ['Installments', 'Intake', 'Badges'],
            transformResponse: dataOf,
        }),

        getDictionary: build.query<CrmDictionaryItem[], void>({
            query: () => ({ url: 'crm/dictionary' }),
            providesTags: ['Dictionary'],
            transformResponse: listOf('items'),
        }),

        saveDictionaryItem: build.mutation<CrmDictionaryItem[], CrmDictionaryItem>({
            query: (body) => ({ url: 'crm/dictionary', method: 'POST', body }),
            invalidatesTags: ['Dictionary', 'Report'],
            transformResponse: listOf('items'),
        }),

        deleteDictionaryItem: build.mutation<CrmDictionaryItem[], { type: CrmDictType; code: string }>({
            query: ({ type, code }) => ({ url: `crm/dictionary/${type}/${encodeURIComponent(code)}`, method: 'DELETE' }),
            invalidatesTags: ['Dictionary'],
            transformResponse: listOf('items'),
        }),

        getHeadSubjects: build.query<CrmSubjectRef[], void>({
            query: () => ({ url: 'crm/head/subjects' }),
            transformResponse: listOf('subjects'),
        }),

        getHeadTracks: build.query<CrmTrackView[], { subjectId?: number; tab: 'NEW' | 'WORK' | 'ALL' }>({
            query: (params) => ({ url: 'crm/head/tracks', params: clean(params) }),
            providesTags: ['Track'],
            transformResponse: listOf('tracks'),
        }),

        getAssignOptions: build.query<CrmAssignGroup[], number>({
            query: (trackId) => ({ url: `crm/tracks/${trackId}/assign-options` }),
            transformResponse: listOf('groups'),
        }),

        assignTrack: build.mutation<CrmTrackView, { trackId: number; groupId: number; mentorId: number }>({
            query: ({ trackId, ...body }) => ({ url: `crm/tracks/${trackId}/assign`, method: 'POST', body }),
            invalidatesTags: ['Track', 'Intake', 'Badges'],
            transformResponse: dataOf,
        }),

        verifyStep: build.mutation<CrmTrackView, { stepId: number; verified: boolean }>({
            query: ({ stepId, verified }) => ({ url: `crm/steps/${stepId}/verify`, method: 'POST', body: { verified } }),
            invalidatesTags: ['Track', 'Intake', 'Badges'],
            transformResponse: dataOf,
        }),

        getMentorTracks: build.query<CrmTrackView[], void>({
            query: () => ({ url: 'crm/mentor/tracks' }),
            providesTags: ['Track'],
            transformResponse: listOf('tracks'),
        }),

        addStepPhotos: build.mutation<CrmTrackView, { trackId: number; stepNo: number; body: FormData }>({
            query: ({ trackId, stepNo, body }) => ({ url: `crm/tracks/${trackId}/steps/${stepNo}/photos`, method: 'POST', body }),
            invalidatesTags: ['Track', 'Intake', 'Badges'],
            transformResponse: dataOf,
        }),

        deleteStepPhoto: build.mutation<CrmTrackView, number>({
            query: (photoId) => ({ url: `crm/photos/${photoId}`, method: 'DELETE' }),
            invalidatesTags: ['Track', 'Intake', 'Badges'],
            transformResponse: dataOf,
        }),

        setFeedback: build.mutation<CrmTrackView, { trackId: number; feedback: CrmFeedback; note: string }>({
            query: ({ trackId, ...body }) => ({ url: `crm/tracks/${trackId}/feedback`, method: 'PUT', body }),
            invalidatesTags: ['Track', 'Intake', 'Badges'],
            transformResponse: dataOf,
        }),

        getSalesReport: build.query<CrmSalesReport, CrmPeriod>({
            query: (params) => ({ url: 'crm/rop/report', params: clean(params) }),
            providesTags: ['Report'],
            transformResponse: dataOf,
        }),

        getManagers: build.query<CrmManager[], CrmPeriod>({
            query: (params) => ({ url: 'crm/rop/managers', params: clean(params) }),
            providesTags: ['Managers'],
            transformResponse: listOf('managers'),
        }),

        addManager: build.mutation<
            { userId: number; login: string; tempPassword: string },
            { name: string; phone: string; salesGroup: string; hiredAt: string }
        >({
            query: (body) => ({ url: 'crm/rop/managers', method: 'POST', body }),
            invalidatesTags: ['Managers', 'Report'],
            transformResponse: dataOf,
        }),

        deleteManager: build.mutation<boolean, number>({
            query: (id) => ({ url: `crm/rop/managers/${id}`, method: 'DELETE' }),
            invalidatesTags: ['Managers'],
            transformResponse: dataOf,
        }),

        // каждый вызов пишется в журнал просмотров (VIEW_PASSWORD) - не кешируем
        getManagerCredentials: build.query<CrmCredentials, number>({
            query: (id) => ({ url: `crm/rop/managers/${id}/credentials` }),
            keepUnusedDataFor: 0,
            transformResponse: dataOf,
        }),

        resetManagerPassword: build.mutation<CrmCredentials, number>({
            query: (id) => ({ url: `crm/rop/managers/${id}/reset-password`, method: 'POST' }),
            transformResponse: dataOf,
        }),

        fireManager: build.mutation<boolean, { id: number; date: string; reason: string }>({
            query: ({ id, ...body }) => ({ url: `crm/rop/managers/${id}/fire`, method: 'POST', body }),
            invalidatesTags: ['Managers'],
            transformResponse: dataOf,
        }),

        rehireManager: build.mutation<boolean, number>({
            query: (id) => ({ url: `crm/rop/managers/${id}/rehire`, method: 'POST' }),
            invalidatesTags: ['Managers'],
            transformResponse: dataOf,
        }),

        getBadges: build.query<CrmBadges, void>({
            query: () => ({ url: 'crm/badges' }),
            providesTags: ['Badges'],
            transformResponse: dataOf,
        }),

        getTelegram: build.query<CrmTelegram, void>({
            query: () => ({ url: 'crm/telegram' }),
            providesTags: ['Telegram'],
            transformResponse: dataOf,
        }),

        unlinkTelegram: build.mutation<CrmTelegram, void>({
            query: () => ({ url: 'crm/telegram', method: 'DELETE' }),
            invalidatesTags: ['Telegram'],
            transformResponse: dataOf,
        }),
    }),
})
